import "./edgeFade.css";

// Imágenes de los items que se desvanecen al llegar a los bordes del carrusel.
export const FADE_MEDIA_SELECTOR =
  ".PlaylistItemImage, .PlaylistCircularItemImage, .PlaylistTextItemImage, .carousel-fade-media";

// Textos que van encima de la imagen; solo se desvanecen (menos que las imágenes) en los carruseles
// marcados con text-over-image (ver edgeFade.css), pero se siguen siempre por simplicidad.
export const FADE_TEXT_SELECTOR =
  ".PlaylistItemFooter, .PlaylistItemContaner, .PlaylistCircularItemFooter, .PlaylistItemTitle, " +
  ".PlaylistItemSubtitle, .ButtonAccess";

const FADE_TRACKED_SELECTOR = `${FADE_MEDIA_SELECTOR}, ${FADE_TEXT_SELECTOR}`;

// La máscara de cada elemento está en sus propias coordenadas, pero el desvanecido depende de dónde está
// respecto a la caja `box` (el carrusel). Se publica en cada elemento su posición en la caja
// (--edge-fade-x) y en la caja su ancho (--edge-fade-width), y se recalcula en cada frame mientras algo
// se mueve (scroll, transición o arrastre del track, hover...) hasta que las posiciones se estabilizan.
// Mientras haya transiciones en curso se sigue recalculando aunque las posiciones no cambien: con
// ease-in-out los primeros frames apenas se mueven y el bucle se pararía antes de que avance el track.
const MAX_TRANSITION_MS = 2000;

export const trackEdgeFade = (box) => {
  let frame = null;
  let stableFrames = 0;
  let lastSignature = "";
  const runningTransitions = new Map();
  let lastTransitionEvent = 0;

  const update = () => {
    const boxRect = box.getBoundingClientRect();
    const elements = Array.from(box.querySelectorAll(FADE_TRACKED_SELECTOR));
    const offsets = elements.map((el) => el.getBoundingClientRect().left - boxRect.left);

    box.style.setProperty("--edge-fade-width", `${boxRect.width}px`);
    elements.forEach((el, i) => el.style.setProperty("--edge-fade-x", `${offsets[i]}px`));

    return `${boxRect.width}|${offsets.join(",")}`;
  };

  const tick = () => {
    const signature = update();
    stableFrames = signature === lastSignature ? stableFrames + 1 : 0;
    lastSignature = signature;
    // Tope por si se pierde algún transitionend (p. ej. elemento eliminado a mitad de la transición).
    if (performance.now() - lastTransitionEvent > MAX_TRANSITION_MS) runningTransitions.clear();
    frame = stableFrames < 3 || runningTransitions.size > 0 ? requestAnimationFrame(tick) : null;
  };

  const schedule = () => {
    stableFrames = 0;
    if (frame === null) frame = requestAnimationFrame(tick);
  };

  // Cambios en el DOM (items nuevos, clones del modo infinito, transform del track al arrastrar...),
  // ignorando las escrituras de --edge-fade-x en los propios elementos seguidos.
  const mutationObserver = new MutationObserver((records) => {
    const relevant = records.some(
      (r) => r.type === "childList" || !(r.target === box || r.target.matches?.(FADE_TRACKED_SELECTOR))
    );
    if (relevant) schedule();
  });
  mutationObserver.observe(box, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "class"],
  });

  const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
  resizeObserver?.observe(box);

  // Transiciones en curso por elemento: Map<elemento, Set<propiedad>>.
  const onTransition = (event) => {
    const { target, propertyName, type } = event;
    const properties = runningTransitions.get(target) || new Set();
    if (type === "transitionrun") properties.add(propertyName);
    else properties.delete(propertyName);
    if (properties.size > 0) runningTransitions.set(target, properties);
    else runningTransitions.delete(target);
    lastTransitionEvent = performance.now();
    schedule();
  };

  const events = ["scroll", "load"];
  const transitionEvents = ["transitionrun", "transitionend", "transitioncancel"];
  events.forEach((name) => box.addEventListener(name, schedule, true));
  transitionEvents.forEach((name) => box.addEventListener(name, onTransition, true));
  window.addEventListener("resize", schedule);

  update();

  return () => {
    if (frame !== null) cancelAnimationFrame(frame);
    mutationObserver.disconnect();
    resizeObserver?.disconnect();
    events.forEach((name) => box.removeEventListener(name, schedule, true));
    transitionEvents.forEach((name) => box.removeEventListener(name, onTransition, true));
    window.removeEventListener("resize", schedule);
  };
};
